import { type FetchOptions, GoogleSyncError, googleFetch, trimSlash } from "./http.js";
import type {
  GoogleCalendarListEntry,
  GoogleEvent,
  GoogleEventsPage,
  GoogleEventWrite,
  ListEventsOptions,
} from "./types.js";

export { GoogleSyncError } from "./http.js";

const API_BASE = "https://www.googleapis.com/calendar/v3";

/**
 * Thin REST client for the Google Calendar API. Authenticates with a per-account
 * bearer access token (`getToken`); on a `401` it calls `refresh` ONCE for a fresh
 * token and retries — exactly mirroring `HttpSyncClient.authedPost`, so a mid-sync
 * token expiry self-heals. A `410 GONE` (expired `syncToken`) surfaces as
 * `GoogleSyncError(410)` for the reconciler to fall back to a full resync.
 */
export class GoogleCalendarClient {
  constructor(
    private readonly getToken: () => string | null,
    private readonly refresh: () => Promise<string | null>,
    private readonly base: string = API_BASE,
  ) {}

  /** The user's calendar list — bind targets. */
  async listCalendars(): Promise<GoogleCalendarListEntry[]> {
    const res = await this.authed<{ items?: GoogleCalendarListEntry[] }>(`/users/me/calendarList`);
    return res.items ?? [];
  }

  /** One page of events for a calendar. Pass `syncToken` for incremental sync OR
   *  `timeMin`/`timeMax` for a bounded full list (not both). `showDeleted` +
   *  `singleEvents:false` so we receive cancellations and master recurrences. */
  listEvents(calendarId: string, opts: ListEventsOptions = {}): Promise<GoogleEventsPage> {
    const q = new URLSearchParams({ showDeleted: "true", singleEvents: "false" });
    if (opts.syncToken) q.set("syncToken", opts.syncToken);
    if (opts.timeMin) q.set("timeMin", opts.timeMin);
    if (opts.timeMax) q.set("timeMax", opts.timeMax);
    if (opts.pageToken) q.set("pageToken", opts.pageToken);
    q.set("maxResults", String(opts.maxResults ?? 250));
    return this.authed<GoogleEventsPage>(`/calendars/${enc(calendarId)}/events?${q.toString()}`);
  }

  getEvent(calendarId: string, eventId: string): Promise<GoogleEvent> {
    return this.authed<GoogleEvent>(`/calendars/${enc(calendarId)}/events/${enc(eventId)}`);
  }

  insertEvent(calendarId: string, body: GoogleEventWrite): Promise<GoogleEvent> {
    return this.authed<GoogleEvent>(`/calendars/${enc(calendarId)}/events`, {
      method: "POST",
      body,
    });
  }

  /** Conditional update: `ifMatch` (the stored etag) makes Google reject with 412
   *  if the remote changed first, so the reconciler can resolve the conflict. */
  patchEvent(
    calendarId: string,
    eventId: string,
    body: GoogleEventWrite,
    ifMatch?: string | null,
  ): Promise<GoogleEvent> {
    const opts: FetchOptions = { method: "PATCH", body };
    if (ifMatch) opts.headers = { "If-Match": ifMatch };
    return this.authed<GoogleEvent>(`/calendars/${enc(calendarId)}/events/${enc(eventId)}`, opts);
  }

  deleteEvent(calendarId: string, eventId: string, ifMatch?: string | null): Promise<void> {
    const opts: FetchOptions = { method: "DELETE" };
    if (ifMatch) opts.headers = { "If-Match": ifMatch };
    return this.authed<void>(`/calendars/${enc(calendarId)}/events/${enc(eventId)}`, opts);
  }

  private async authed<T>(path: string, opts: FetchOptions = {}): Promise<T> {
    const url = trimSlash(this.base) + path;
    const token = this.getToken();
    if (!token) throw new GoogleSyncError("Not connected to Google", 401);
    try {
      return await googleFetch<T>(url, token, opts);
    } catch (e) {
      if (e instanceof GoogleSyncError && e.status === 401) {
        const fresh = await this.refresh();
        if (fresh) return googleFetch<T>(url, fresh, opts);
      }
      throw e;
    }
  }
}

/** Calendar ids contain `@` and `:`; event ids are opaque — encode both. */
function enc(segment: string): string {
  return encodeURIComponent(segment);
}
